import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { dateTimeToS, dateUtcToS, dateOnlyFormat, getCurrentDateTime } from '../../util/dateUtil';
import { makeStyles } from '@material-ui/core/styles';
import {
  FormLabel,
  Button,
  Checkbox,
  Grid,
  Hidden,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
  Tooltip,
  TextField,
  Typography,
  TablePagination,
  RadioGroup,
  Radio,
} from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import TableStyle from '../../styles/TableStyle';
import { observer, inject } from 'mobx-react';
import { logException } from '../../services/appInsights';
import PleaseWait from '../Shared/PleaseWait';
import { callApi } from '../../util/apiWrapper';
import TitleBar from '../Shared/TitleBar';
import ScholarshipApplicationCards from './ScholarshipApplicationCards';
import PageUnauthorized from '../Shared/PageUnauthorized';
import { titleize } from '../../util/stringUtil';
import Card from '../Card/Card.js';
import CardBody from '../Card/CardBody.js';
import AwardDialog from './ScholarshipDialogs/AwardDialog';
import { convertSiteCode, isStudent, isAdmin } from '../../util/miscUtil';
import { calculatePlanningScore, displayApplicantName, getSortName, updateAwardTotals } from '../../util/scholarshipsUtil';
import { useScholarshipApplicationListStyles } from './ScholarshipApplicationList.styles';
import {
  displayStatus,
  createYearSelectHandler,
  getBeginYear,
  getEndYear,
  filterForAcademicYear,
  fetchAllScholarshipAwards,
  handleCardSortChange,
  handleShowAll,
  closeAwardsDialog,
  deleteAward,
  createAward,
  updateAward,
  filterData,
  isShowAllDisabled,
  handleOpenAwardsDialog,
  updateAwardAmount,
  handleOpenScholarshipApp,
  updateApplicationStatus,
  updateAwardScholarship,
  updateAwardStatus,
  updateAwardNotes,
  updateAwardedOn,
} from './ScholarshipApplicationUtils';

const tableStyles = makeStyles(TableStyle);

const ScholarshipApplicationList = inject(
  'SeniorStore',
  'AuthStore'
)(
  observer((props) => {
    const store = props.SeniorStore;
    const auth = props.AuthStore;
    const history = useHistory();

    const classes = useScholarshipApplicationListStyles();
    const tableClasses = tableStyles();

    const [pageState, setPageState] = useState({
      allRecords: [],
      records: [],
      filter: '',
      fetchedRecords: false,
      loading: false,
      showAllRecords: false,
      sortDirection: 'asc',
      sortBy: '',
    });
    const [errorMsg, setErrorMsg] = useState(null);
    const [pageOptions, setPageOptions] = useState({
      searching: false,
      page: 0,
      perPage: 15,
    });
    const [filter, setFilter] = React.useState('');
    const [totalApps, setTotalApps] = React.useState(0);
    const [completedApps, setCompletedApps] = React.useState(0);
    const [completedPercent, setCompletedPercent] = React.useState(0);
    const [pageUserId, setPageUserId] = React.useState(0);

    const [showAwardsDialog, setShowAwardsDialog] = React.useState(false);
    const [appClicked, setAppClicked] = React.useState({});
    const [appSenior, setAppSenior] = React.useState({});
    const [showAddAward, setShowAddAward] = React.useState(false);
    const [awardPendingTotal, setAwardPendingTotal] = React.useState(0);
    const [awardAwardedTotal, setAwardAwardedTotal] = React.useState(0);
    const [awardTotal, setAwardTotal] = React.useState(0);
    const [totalAmountAwarded, setTotalAmountAwarded] = React.useState(0);
    const [totalAwardedCount, setTotalAwardedCount] = React.useState(0);
    const [seniorPlanning1, setSeniorPlanning1] = React.useState({});
    const [seniorPlanning2, setSeniorPlanning2] = React.useState({});
    const [seniorPlanningScore, setSeniorPlanningScore] = React.useState(0);
    const [originalStatus, setOriginalStatus] = React.useState('');
    const [appAwardsFetched, setAppAwardsFetched] = React.useState(false);
    const [yearsList, setYearsList] = React.useState([]);
    const [academicYear, setAcademicYear] = React.useState('');
    const [initialized, setInitialized] = React.useState(false);
    const [initialFetchDone, setInitialFetchDone] = React.useState(false);

    const fetchRecords = useCallback(
      async (year) => {
        try {
          setPageState((prevState) => ({ ...prevState, loading: true }));

          // Fetch all submitted scholarships (URL doesn't really describe functionality)
          const rsp = await callApi(
            auth,
            `scholarship/submitted/returned?siteList=${store.staffsites}&showAll=${store.showCompletedScholarshipApps}`,
            'GET'
          );

          const begin = dateOnlyFormat(getBeginYear(year));
          const end = dateOnlyFormat(getEndYear(year));

          if (!begin || !end) {
            setPageState((prevState) => ({ ...prevState, loading: false, fetchedRecords: false }));
            setErrorMsg('Invalid academic year selected.');
            return;
          }

          // FIX: Changed from submitted_date to status_date STRY0052444
          const first = rsp.result.filter((s) => dateOnlyFormat(s.status_date) >= begin);
          const second = first.filter((s) => dateOnlyFormat(s.status_date) <= end);
          const third = second.filter(
            (rec) =>
              rec.status === 'submitted' ||
              rec.status === 'returned' ||
              rec.status === 'reviewed' ||
              rec.status === 'awarded' ||
              rec.status === 'denied' ||
              rec.status === 'qualified' ||
              rec.status === 'qualifiedinitial' ||
              rec.status === 'qualifiedprogram' ||
              rec.status === 'underreview'
          );

          for (let i = 0; i < third.length; i++) {
            const rec = third[i];
            rec.name = getSortName(rec.first_name, rec.last_name, rec.alumni_updated_first_name, rec.alumni_updated_last_name);
            rec.displayStatus = displayStatus(rec.status);

            // Calculate award totals for this application
            let pendingAwards = 0;
            let awardedAwards = 0;
            let awardedAmount = 0;

            if (store.allAwards && store.allAwards.length > 0) {
              store.allAwards.forEach((award) => {
                if (award.scholarship_application_id === rec.id) {
                  if (award.status === 'pending') {
                    pendingAwards++;
                  } else if (award.status === 'awarded') {
                    awardedAwards++;
                    awardedAmount += parseInt(award.amount || 0, 10);
                  }
                }
              });
            }

            rec.pendingAwards = pendingAwards;
            rec.awardedAwards = awardedAwards;
            rec.awardedAmount = awardedAmount;
          }

          setPageState((prevState) => ({
            ...prevState,
            loading: false,
            records: [...third],
            allRecords: [...third],
            sortBy: 'status',
            fetchedRecords: true,
          }));
          setInitialFetchDone(true);
        } catch (error) {
          logException(error);
          setPageState((prevState) => ({ ...prevState, loading: false, fetchedRecords: false }));
          setErrorMsg('An unexpected error has occurred. Please try again.');
        }
      },
      [auth, store.staffsites, store.showCompletedScholarshipApps, store.allAwards]
    );

    const handleYearSelectChange = createYearSelectHandler(yearsList, store, setAcademicYear, fetchRecords, setPageOptions);

    // Initialize yearsList and scholarships on mount
    useEffect(() => {
      if (!auth || !auth.user || initialized) {
        return;
      }

      const getAcademicYearsList = () => {
        let academicYears = [];

        const year = new Date().getFullYear();
        const firstYear = 2022;
        const lastYear = year;
        let start = firstYear;
        let end = firstYear + 1;

        for (var i = firstYear; i < lastYear + 1; i++) {
          academicYears.push({ 'value': start + ' - ' + end, 'label': start + ' - ' + end });
          start = start + 1;
          end = end + 1;
        }
        academicYears.push({ 'value': firstYear + ' - ' + (end - 1), 'label': 'All Records' });
        setYearsList(academicYears);

        // Default selected to latest year (last row is all, so setting to 2nd to last row)
        const defaultYear = academicYears[academicYears.length - 2].value;
        setAcademicYear(defaultYear);
        return defaultYear;
      };

      const fetchScholarships = async () => {
        try {
          const today = getCurrentDateTime();
          const list = await callApi(auth, `scholarships/list`, 'GET');
          const filteredList = list.result
            .filter((s) => dateOnlyFormat(s.begin_date) <= dateOnlyFormat(today) && (s.end_date >= dateOnlyFormat(today) || !s.end_date))
            .sort((a, b) => {
              return a.name > b.name ? 1 : -1;
            });
          store.setScholarships(filteredList);
        } catch (error) {
          logException(error);
          setErrorMsg('An unexpected error has occurred. Please try again.');
        }
      };

      setInitialized(true);
      getAcademicYearsList();
      fetchScholarships();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.user]);

    // Fetch staff sites
    useEffect(() => {
      const fetchStaffSites = async () => {
        let siteList = [];
        try {
          if (store.staffsites.length === 0) {
            const email = auth.user.result.email;
            const sites = await callApi(auth, 'staff/sites/' + email, 'GET');
            sites.result.forEach((s, i) => {
              siteList.push(convertSiteCode(s.node_id));
            });
            store.setStaffSites(siteList);
          }
        } catch (error) {
          logException(error);
          console.warn('Error fetching staff sites:', error);
        }
      };

      if (auth && auth.user) {
        fetchStaffSites();
        setPageUserId(auth.user.result.id);
      }
    }, [auth, auth.user, store]);

    // Fetch records when staff sites and academic year are ready
    useEffect(() => {
      if (store.staffsites && store.staffsites.length > 0 && academicYear && !initialFetchDone) {
        fetchRecords(academicYear);
      }
    }, [store.staffsites, academicYear, fetchRecords, initialFetchDone]);

    useEffect(() => {
      if (store.staffsites && store.staffsites.length > 0 && !pageState.loading) {
        fetchAllScholarshipAwards(auth, store, setErrorMsg);
        if (auth && auth.user && auth.user.result && auth.user.result.id) {
          setPageUserId(auth.user.result.id);
        }
      }
    }, [auth, store, store.staffsites, pageState.loading]);

    useEffect(() => {
      const fetchScholarshipStats = async () => {
        try {
          setTotalApps(0);
          const begin = dateOnlyFormat(getBeginYear(academicYear));
          const end = dateOnlyFormat(getEndYear(academicYear));
          if (!begin || !end) {
            console.warn('Invalid begin or end date for academic year:', academicYear);
            setCompletedPercent(0);
            setTotalApps(0);
            return;
          }

          if (store.staffsites && store.staffsites.length > 0 && begin && end) {
            const scholarshipStats = await callApi(
              auth,
              `scholarship/dashboard/year?siteList=${store.staffsites}&startDate=${begin}&endDate=${end}`,
              'GET'
            );

            const totalApps =
              scholarshipStats.completedApps +
              scholarshipStats.pendingApps +
              scholarshipStats.qualifiedApps +
              scholarshipStats.reviewedApps +
              scholarshipStats.qualifiedApps +
              scholarshipStats.returnedApps;
            setCompletedApps(scholarshipStats.completedApps);

            setTotalApps(totalApps);
            if (totalApps > 0) {
              setCompletedPercent((scholarshipStats.completedApps / totalApps) * 100);
            } else {
              setCompletedPercent(0);
            }
          } else {
            setCompletedPercent(0);
            setTotalApps(0);
          }
        } catch (error) {
          logException(error);
          setErrorMsg('An unexpected error has occurred. Please try again.');
        }
      };
      const getTotalAwards = () => {
        let total = 0;
        let count = 0;

        const filteredData = filterForAcademicYear(academicYear, store.allAwards);

        filteredData.forEach((a) => {
          if (a.status === 'awarded') {
            total += parseInt(a.amount || 0, 10);
            count++;
          }
        });

        setTotalAmountAwarded(total);
        setTotalAwardedCount(count);
      };

      if (store.staffsites && store.staffsites.length > 0 && academicYear) {
        fetchScholarshipStats();
      }
      if (store.allAwards && store.allAwards.length > 0 && academicYear) {
        getTotalAwards();
      }
      // }, [store.allAwards, academicYear, auth, filterForAcademicYear, store.staffsites]);
    }, [store.allAwards, academicYear, auth, store.staffsites]);

    useEffect(() => {
      try {
        if (seniorPlanning1 || seniorPlanning2) {
          const plan1 = seniorPlanning1 && seniorPlanning1.planning_form_1_data ? JSON.parse(seniorPlanning1.planning_form_1_data) : null;
          const plan2 = seniorPlanning2 && seniorPlanning2.planning_form_2_data ? JSON.parse(seniorPlanning2.planning_form_2_data) : null;
          const planningScore = calculatePlanningScore(plan1, plan2);
          setSeniorPlanningScore(planningScore);
        }
      } catch (e) {
        console.warn('Error reading planning form scores: ', e);
      }
    }, [seniorPlanning1, seniorPlanning2]);

    useEffect(() => {
      if (auth.roles && auth.roles.length > 0) {
        if (isStudent(auth.roles)) {
          history.push('/seniorplanner');
        }
      }
    }, [auth.roles, history]);

    useEffect(() => {
      if (appAwardsFetched) {
        const totalResults = updateAwardTotals(store.appAwards);
        setAwardPendingTotal(totalResults.pendingTotal);
        setAwardAwardedTotal(totalResults.awardedTotal);
        setAwardTotal(totalResults.total);
        setShowAwardsDialog(true);
      }
    }, [store.appAwards, appAwardsFetched]);

    useEffect(() => {
      const compareValues = (key, order = 'asc') => {
        return function innerSort(a, b) {
          const varA = !a.hasOwnProperty(key) ? '' : key === 'name' || key === 'displayStatus' ? a[key].toLowerCase() : a[key];
          const varB = !b.hasOwnProperty(key) ? '' : key === 'name' || key === 'displayStatus' ? b[key].toLowerCase() : b[key];
          let comparison = 0;
          if (varA > varB) {
            comparison = 1;
          } else if (varA < varB) {
            comparison = -1;
          }
          return order === 'desc' ? comparison * -1 : comparison;
        };
      };

      let filteredData = pageState.allRecords;

      if (filter && filter.length > 0) {
        const filterNoSpaces = (filter || '').replace(/\s+/g, '');
        const filterLower = filterNoSpaces.toLowerCase();
        // This needs to be here, because "returned" and "Ready for Committee Review" and "reviewed" start with "re"
        if (filterLower === 'r' || filterLower === 're') {
          filteredData = filteredData.filter((s) => s.status === 'qualified' || s.status === 'returned' || s.status === 'reviewed');
        } else if (filterLower.startsWith('rea') || filterLower.startsWith('read')) {
          filteredData = filteredData.filter((s) => s.status === 'qualified');
        } else {
          filteredData = filteredData.filter(
            (s) =>
              (s.applicant_type && s.applicant_type.toLowerCase().includes(filterLower)) ||
              (s.status && s.status.toLowerCase().includes(filterLower)) ||
              (s.first_name && s.first_name.toLowerCase().includes(filterLower)) ||
              (s.last_name && s.last_name.toLowerCase().includes(filterLower)) ||
              (s.alumni_updated_first_name && s.alumni_updated_first_name.toLowerCase().includes(filterLower)) ||
              (s.alumni_updated_last_name && s.alumni_updated_last_name.toLowerCase().includes(filterLower)) ||
              (s.alumni_updated_first_name && s.alumni_updated_first_name.toLowerCase().includes(filterLower)) ||
              (s.alumni_updated_last_name && s.alumni_updated_last_name.toLowerCase().includes(filterLower)) ||
              (s.submitted_date && dateUtcToS(s.submitted_date).toString().includes(filter))
          );
        }
      }

      let sortedRecords = filteredData;
      if (sortedRecords) {
        if (!pageState.showAllRecords) {
          sortedRecords = sortedRecords.filter((x) => !x.approved);
        }
        sortedRecords.sort(compareValues(pageState.sortBy, pageState.sortDirection));
        setPageState((p) => {
          return { ...p, records: sortedRecords };
        });
      }
    }, [pageState.showAllRecords, pageState.allrecords, pageState.sortBy, pageState.sortDirection, pageState.allRecords, filter]);

    const setSort = (val) => {
      let direction = 'asc';
      if (val === pageState.sortBy) {
        if (pageState.sortDirection === 'asc') {
          direction = 'desc';
        } else {
          direction = 'asc';
        }
        setPageState({ ...pageState, sortDirection: direction });
      } else {
        setPageState({ ...pageState, sortDirection: 'asc', sortBy: val });
      }
    };

    const emptyRows =
      pageOptions.perPage - Math.min(pageOptions.perPage, pageState.records && pageState.records.length - pageOptions.page * pageOptions.perPage);

    if (auth.roles && auth.roles.length > 0 && !isAdmin(auth.roles)) {
      return <PageUnauthorized />;
    }

    return (
      <div className={classes.frame}>
        {auth.roles && auth.roles.length > 0 && isAdmin(auth.roles) && (
          <Grid container className={classes.whiteBg}>
            <React.Fragment>
              <PleaseWait isLoading={pageState.loading} />
              <TitleBar title='Scholarship Applications' currentPage='Scholarship Applications' paths={[{ path: '/', name: 'Home' }]} />

              <div className={classes.frame}>
                {totalApps > -1 && (
                  <div className={classes.alignPage}>
                    <Grid container className={classes.formatTableSize} align='left'>
                      <Grid item xs={12} sm={7} md={4} lg={3} className={classes.cardContainer} align='left'>
                        <Card className={classes.card}>
                          <CardBody className={classes.completedCardContent}>
                            {auth && auth.user && (
                              <summary>
                                <div className={classes.completed}>Completed</div>
                                <div className={classes.totalSummary}>
                                  {completedApps} out of {totalApps} applications
                                </div>
                                <div className={classes.awardAmount}>{completedPercent.toFixed(0)}%</div>
                              </summary>
                            )}

                            <svg width='95px' height='95px' viewBox='0 0 42 42' class='donut' style={{ marginRight: 10 }}>
                              <circle class='donut-hole' cx='21' cy='21' r='15.91549430918954' fill='#fff'></circle>
                              <circle
                                class='donut-ring'
                                cx='21'
                                cy='21'
                                r='15.91549430918954'
                                fill='transparent'
                                stroke='rgba(255, 185, 70, 1)'
                                stroke-width='4'
                              ></circle>
                              <circle
                                class='donut-segment'
                                cx='21'
                                cy='21'
                                r='15.91549430918954'
                                fill='transparent'
                                stroke='rgba(5, 115, 29, 1)'
                                stroke-width='4'
                                stroke-dasharray={[completedPercent, 100]}
                                stroke-dashoffset='0'
                              ></circle>
                            </svg>
                          </CardBody>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={7} md={4} lg={3} className={classes.cardContainer} align='left'>
                        <Card className={classes.card} style={{ maxWidth: '235px' }}>
                          <CardBody className={classes.awardedCardContent}>
                            <summary>
                              <div className={classes.completed}>Total Awarded</div>
                              <div className={classes.totalSummary}>
                                {totalAwardedCount} awards to {totalApps} applicants
                              </div>
                            </summary>
                            <div className={classes.awardAmount}>
                              {totalAmountAwarded &&
                                totalAmountAwarded > 0 &&
                                totalAmountAwarded.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })}
                            </div>
                          </CardBody>
                        </Card>
                      </Grid>
                      <Grid item xs={false} sm={false} md={5} lg={7} align='right' className={classes.whiteBg}>
                        <br />
                        <br />
                      </Grid>
                    </Grid>
                  </div>
                )}
              </div>

              <div className={classes.frame}>
                <div className={classes.alignPage}>
                  <Grid container className={classes.formatTableSize}>
                    <Grid item xs={12}>
                      <FormLabel error>{errorMsg}</FormLabel>
                    </Grid>

                    <Grid item xs={12} className={classes.whiteBg} align='left'>
                      <Grid item xs={6} className={classes.whiteBg} align='left'></Grid>
                      <Grid item xs={6} align='right' className={classes.whiteBg}></Grid>

                      <Grid item xs={12}>
                        <Grid container direction='row'>
                          <Grid item xs={12} sm={12} md={4} lg={3} className={classes.whiteBg} align='left'>
                            <TextField
                              id='filter-field'
                              type='search'
                              className={classes.filter}
                              size='small'
                              hiddenLabel
                              placeholder='Filter'
                              variant='outlined'
                              onChange={(event) => filterData(event.target.value, setFilter)}
                              clearable='true'
                            />
                          </Grid>
                          <Grid item xs={12} sm={12} md={4} lg={3} className={classes.whiteBg} align='left'>
                            <div style={{ marginTop: 10, marginLeft: 15 }}>
                              <span style={{ margin: '20px 10px 0 10px' }}>
                                <strong>Academic Year:</strong>
                              </span>
                              <FormControl>
                                <Select
                                  labelId='academic-year-label'
                                  id='academic-year'
                                  value={academicYear}
                                  onChange={(event) => handleYearSelectChange(event.target.value)}
                                  className={classes.headerSelect}
                                >
                                  {yearsList.slice().map((year) => {
                                    return (
                                      <MenuItem value={year.value} key={year.value} style={{ fontSize: '.9rem' }}>
                                        {year.label}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </div>
                          </Grid>
                          <Grid item xs={12} sm={12} md={4} lg={6} align='left' className={classes.whiteBg}>
                            <div style={{ marginTop: 10, marginLeft: 20 }}>
                              <FormControlLabel
                                control={<Checkbox color='default' />}
                                label='Show Completed/Returned Applications'
                                onChange={(ev) => handleShowAll(store, fetchRecords, academicYear)}
                                checked={store.showCompletedScholarshipApps}
                                disabled={isShowAllDisabled(yearsList, academicYear)}
                              />
                            </div>
                          </Grid>
                        </Grid>
                        <Hidden mdDown implementation='css'>
                          <TableContainer component={Paper}>
                            <Table className={tableClasses.table}>
                              <TableHead>
                                <TableRow>
                                  <TableCell style={{ width: '160px' }}></TableCell>
                                  <Tooltip title='Click to sort by name.' placement='top'>
                                    <TableCell onClick={() => setSort('name')} className={classes.sortHeader} style={{ width: '200px' }}>
                                      YOUNG ADULT{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display:
                                            pageState.sortBy === 'name' ? (pageState.sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display:
                                            pageState.sortBy === 'name' ? (pageState.sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by status.' placement='top'>
                                    <TableCell onClick={() => setSort('displayStatus')} className={classes.sortHeader} style={{ width: '250px' }}>
                                      STATUS{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display:
                                            pageState.sortBy === 'displayStatus'
                                              ? pageState.sortDirection === 'asc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display:
                                            pageState.sortBy === 'displayStatus'
                                              ? pageState.sortDirection === 'desc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by submitted date.' placement='top'>
                                    <TableCell onClick={() => setSort('submitted_date')} className={classes.sortHeader} style={{ width: '160px' }}>
                                      SUBMITTED ON{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display:
                                            pageState.sortBy === 'submitted_date'
                                              ? pageState.sortDirection === 'asc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display:
                                            pageState.sortBy === 'submitted_date'
                                              ? pageState.sortDirection === 'desc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by number of pending awards.' placement='top'>
                                    <TableCell className={classes.noSortHeader} style={{ width: '120px' }}>
                                      <TableCell onClick={() => setSort('pendingAwards')} className={classes.sortHeader} style={{ width: '100px' }}>
                                        # PENDING{' '}
                                        <span
                                          className='fas fa-caret-up'
                                          style={{
                                            display:
                                              pageState.sortBy === 'pendingAwards'
                                                ? pageState.sortDirection === 'asc'
                                                  ? 'inline-block'
                                                  : 'none'
                                                : 'none',
                                          }}
                                        />
                                        <span
                                          className='fas fa-caret-down'
                                          style={{
                                            display:
                                              pageState.sortBy === 'pendingAwards'
                                                ? pageState.sortDirection === 'desc'
                                                  ? 'inline-block'
                                                  : 'none'
                                                : 'none',
                                          }}
                                        />
                                      </TableCell>
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by number of awarded awards.' placement='top'>
                                    <TableCell className={classes.noSortHeader} style={{ width: '120px' }}>
                                      <TableCell onClick={() => setSort('awardedAwards')} className={classes.sortHeader} style={{ width: '100px' }}>
                                        # AWARDED{' '}
                                        <span
                                          className='fas fa-caret-up'
                                          style={{
                                            display:
                                              pageState.sortBy === 'awardedAwards'
                                                ? pageState.sortDirection === 'asc'
                                                  ? 'inline-block'
                                                  : 'none'
                                                : 'none',
                                          }}
                                        />
                                        <span
                                          className='fas fa-caret-down'
                                          style={{
                                            display:
                                              pageState.sortBy === 'awardedAwards'
                                                ? pageState.sortDirection === 'desc'
                                                  ? 'inline-block'
                                                  : 'none'
                                                : 'none',
                                          }}
                                        />
                                      </TableCell>
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by amount of awards.' placement='top'>
                                    <TableCell
                                      onClick={() => setSort('awardedAmount')}
                                      className={classes.sortHeader}
                                      style={{ width: '150px', textAlign: 'center' }}
                                    >
                                      $ AWARDED{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display:
                                            pageState.sortBy === 'awardedAmount'
                                              ? pageState.sortDirection === 'asc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display:
                                            pageState.sortBy === 'awardedAmount'
                                              ? pageState.sortDirection === 'desc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {pageOptions.searching ? (
                                  <TableRow>
                                    <TableCell align='center' colSpan={8}>
                                      <Typography variant='h5'>Searching...</Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  pageState.records &&
                                  pageState.records
                                    .slice(pageOptions.page * pageOptions.perPage, pageOptions.page * pageOptions.perPage + pageOptions.perPage)
                                    .map((record, idx) => (
                                      <TableRow key={record.id} className={classes.tableRow}>
                                        <TableCell
                                          style={{
                                            width: '100px',
                                            textAlign: 'center',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Button
                                            color='primary'
                                            variant='outlined'
                                            className={classes.approvalLink}
                                            aria-describedby='approve request'
                                            onClick={(ev) =>
                                              handleOpenAwardsDialog(
                                                ev,
                                                record,
                                                record.id,
                                                record.applicant_type,
                                                auth,
                                                store,
                                                pageState,
                                                setPageState,
                                                setAppClicked,
                                                setOriginalStatus,
                                                setAppAwardsFetched,
                                                setErrorMsg,
                                                setAppSenior,
                                                setSeniorPlanning1,
                                                setSeniorPlanning2
                                              )
                                            }
                                          >
                                            AWARDS
                                          </Button>
                                          <Button
                                            color='default'
                                            variant='outlined'
                                            className={classes.approvalLink}
                                            aria-describedby='approve request'
                                            onClick={(ev) =>
                                              handleOpenScholarshipApp(
                                                ev,
                                                record.id,
                                                record.applicant_type,
                                                auth,
                                                store,
                                                pageState,
                                                setPageState,
                                                history
                                              )
                                            }
                                          >
                                            REVIEW
                                          </Button>
                                        </TableCell>
                                        <TableCell style={{ width: '200px' }}>
                                          {/* {JSON.stringify(record)} */}
                                          {displayApplicantName(record)}{' '}
                                          {record.applicant_type && record.applicant_type.length > 0 && record.applicant_type === 'alumni'
                                            ? '(' + titleize(record.applicant_type) + ')'
                                            : ''}
                                        </TableCell>
                                        <TableCell style={{ width: '250px' }}>{displayStatus(record.status)}</TableCell>
                                        <TableCell style={{ width: '160px' }}>{dateTimeToS(record.submitted_date)}</TableCell>
                                        <TableCell style={{ width: '100px', textAlign: 'center' }}>{record.pendingAwards}</TableCell>
                                        <TableCell style={{ width: '100px', textAlign: 'center' }}>{record.awardedAwards}</TableCell>
                                        <TableCell style={{ width: '150px', textAlign: 'center' }}>
                                          {record.awardedAmount &&
                                            record.awardedAmount > 0 &&
                                            parseInt(record.awardedAmount, 10).toLocaleString('en-US', {
                                              style: 'currency',
                                              currency: 'USD',
                                            })}
                                        </TableCell>
                                      </TableRow>
                                    ))
                                )}
                                {/* application essayText asvabScore gradDate alumniSignature */}
                                {!pageOptions.searching && pageState.records && pageState.records.length === 0 ? (
                                  <TableRow>
                                    <TableCell align='center' colSpan={8} className={classes.noLeftPadding}>
                                      <summary className={classes.noSeniorsMsg}>No records found.</summary>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  <React.Fragment />
                                )}
                                {emptyRows > 0 && (
                                  <TableRow style={{ height: 34 * emptyRows }}>
                                    <TableCell colSpan={8} />
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                            <TablePagination
                              component='div'
                              colSpan={8}
                              SelectProps={{
                                inputProps: { 'aria-label': 'rows per page' },
                                menuProps: { classes: { paper: classes.selectDropdown } },
                              }}
                              classes={{ menuItem: classes.menuItem }}
                              rowsPerPageOptions={[10, 15, 25, 50, 100]}
                              count={pageState.records ? pageState.records.length : 0}
                              rowsPerPage={pageOptions.perPage}
                              page={pageOptions.page}
                              onChangePage={(ev, val) => {
                                setPageOptions({ ...pageOptions, page: val });
                              }}
                              onRowsPerPageChange={(ev) => setPageOptions({ ...pageOptions, perPage: parseInt(ev.target.value, 10), page: 0 })}
                              showFirstButton
                              showLastButton
                            />
                          </TableContainer>
                          <div className={classes.dataExplanation}>
                            NOTE: The totals at the top of the page may not match the data in the table if the dates between the scholarship
                            applications and scholarship awards land in different years. The totals at the top are based on the scholarship award
                            information, while the information in the table is based on the scholarship applications, although the # PENDING, #
                            AWARDED, and $ AWARDED information is then looked up in the awards table, if the application is included in the table,
                            based on the application date.
                          </div>
                        </Hidden>

                        <Hidden lgUp implementation='css'>
                          <Grid container>
                            <Grid item xs={12} sm={7} md={5} className={classes.radioRow}>
                              <RadioGroup
                                aria-label='card-sorting'
                                name='date-group'
                                className={classes.centerRadioSorting}
                                row
                                value={pageState.sortBy}
                                onChange={(event) => handleCardSortChange(event, pageState, setPageState)}
                              >
                                <FormControlLabel
                                  value='status_by'
                                  control={<Radio color='default' />}
                                  label={<span className={classes.radioLabel}>Sort by Name</span>}
                                  onClick={() => setSort('status_by')}
                                  className={classes.radioLabel}
                                />
                                <aside
                                  style={{
                                    marginLeft: '-15px',
                                    marginTop: '8px',
                                    marginRight: '10px',
                                  }}
                                >
                                  <span
                                    className='fas fa-caret-up'
                                    style={{
                                      display:
                                        pageState.sortBy === 'status_by' ? (pageState.sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                    }}
                                  />
                                  <span
                                    className='fas fa-caret-down'
                                    style={{
                                      display:
                                        pageState.sortBy === 'status_by' ? (pageState.sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                    }}
                                  />
                                </aside>
                                <FormControlLabel
                                  value='submission_date'
                                  control={<Radio color='default' />}
                                  label={<span className={classes.radioLabel}>Sort by Date</span>}
                                  onClick={() => setSort('submission_date')}
                                  className={classes.radioLabel}
                                  style={{
                                    marginLeft: '20px',
                                  }}
                                />
                                <aside
                                  style={{
                                    marginLeft: '-15px',
                                    marginTop: '8px',
                                    marginRight: '10px',
                                  }}
                                >
                                  <span
                                    className='fas fa-caret-up'
                                    style={{
                                      display:
                                        pageState.sortBy === 'submission_date'
                                          ? pageState.sortDirection === 'asc'
                                            ? 'inline-block'
                                            : 'none'
                                          : 'none',
                                    }}
                                  />
                                  <span
                                    className='fas fa-caret-down'
                                    style={{
                                      display:
                                        pageState.sortBy === 'submission_date'
                                          ? pageState.sortDirection === 'desc'
                                            ? 'inline-block'
                                            : 'none'
                                          : 'none',
                                    }}
                                  />
                                </aside>
                              </RadioGroup>
                            </Grid>
                          </Grid>
                          <ScholarshipApplicationCards
                            list={pageState.records}
                            open={(ev, id, type) => handleOpenScholarshipApp(ev, id, type, auth, store, pageState, setPageState, history)}
                            showAwards={(ev, record, id, type) =>
                              handleOpenAwardsDialog(
                                ev,
                                record,
                                id,
                                type,
                                auth,
                                store,
                                pageState,
                                setPageState,
                                setAppClicked,
                                setOriginalStatus,
                                setAppAwardsFetched,
                                setErrorMsg,
                                setAppSenior,
                                setSeniorPlanning1,
                                setSeniorPlanning2
                              )
                            }
                            displayStatus={displayStatus}
                            displayApplicantName={displayApplicantName}
                          />
                        </Hidden>
                      </Grid>
                    </Grid>
                  </Grid>
                </div>
              </div>
              <AwardDialog
                showDialog={showAwardsDialog}
                setShowDialog={setShowAwardsDialog}
                handleClose={() => closeAwardsDialog(setShowAwardsDialog)}
                application={appClicked}
                senior={appSenior}
                user={pageUserId}
                scholarships={store.scholarships}
                appAwards={store.appAwards}
                setAppAwards={store.setAppAwards}
                showAddAward={showAddAward}
                setShowAddAward={setShowAddAward}
                updateAward={(award) =>
                  updateAward(
                    award,
                    auth,
                    store,
                    pageUserId,
                    pageState,
                    setPageState,
                    setAwardPendingTotal,
                    setAwardAwardedTotal,
                    setAwardTotal,
                    fetchAllScholarshipAwards,
                    setErrorMsg
                  )
                }
                createAward={(award) =>
                  createAward(award, auth, store, appSenior, pageUserId, pageState, setPageState, () =>
                    fetchAllScholarshipAwards(auth, store, setErrorMsg)
                  )
                }
                updateAwardScholarship={(rec, scholarship_id) => updateAwardScholarship(rec, scholarship_id, store)}
                updateAwardStatus={(rec, status) => updateAwardStatus(rec, status, store)}
                updateAwardAmount={(rec, amount) => updateAwardAmount(rec, amount, store)}
                updateAwardNotes={(rec, notes) => updateAwardNotes(rec, notes, store)}
                updateAwardedOn={(rec, awardedDate) => updateAwardedOn(rec, awardedDate, store)}
                awardPendingTotal={awardPendingTotal}
                awardAwardedTotal={awardAwardedTotal}
                awardTotal={awardTotal}
                seniorPlanningScore={seniorPlanningScore}
                updateApplicationStatus={(id, status) =>
                  updateApplicationStatus(id, status, auth, store, pageState, setPageState, originalStatus, setAppClicked, appClicked)
                }
                deleteAward={(record) =>
                  deleteAward(record, auth, store, pageUserId, pageState, setPageState, () => fetchAllScholarshipAwards(auth, store, setErrorMsg))
                }
                parent='applist'
              />
            </React.Fragment>
          </Grid>
        )}
      </div>
    );
  })
);

export default ScholarshipApplicationList;
